/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import redirect from 'js/lib/coursera.redirect';
import keysToConstants from 'js/lib/keysToConstants';
import { LONG_DATE_ONLY_DISPLAY } from 'js/utils/DateTimeUtils';

import { Button, Dialog, Radio, RadioGroup, Typography2 } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import PrePurchaseUpsellAllPlansModal from 'bundles/coursera-plus-common/components/prePurchaseUpsell/PrePurchaseUpsellAllPlansModal';
import {
  getFormattedPartnerDisplayName,
  usePrePurchaseUpsellEnrollment,
} from 'bundles/coursera-plus-common/utils/prePurchaseUpsellUtils';
import type { EnrollmentChoiceTypesValues } from 'bundles/enroll-course/common/EnrollmentChoiceTypes';
import {
  AUDIT_COURSE,
  ENROLL_COURSE,
  PURCHASE_SINGLE_COURSE,
  SUBSCRIBE_TO_COURSERA_PLUS,
} from 'bundles/enroll-course/common/EnrollmentChoiceTypes';
import type EnrollmentChoiceTypes from 'bundles/enroll-course/common/EnrollmentChoiceTypes';
import CourseEnrollmentConfirmation from 'bundles/enroll-course/components/CourseEnrollmentConfirmation';
import EnrollmentReasonMessageV2 from 'bundles/enroll-course/components/EnrollmentReasonMessageV2';
import {
  canPurchaseSingleCourseAndHasFullDiscount,
  enrollWithFullDiscountPromotion,
} from 'bundles/enroll-course/lib/apiClient';
import {
  choiceTypeToHandleSubmitPromise,
  submitEnrollmentPromise,
} from 'bundles/enroll-course/lib/enrollmentChoiceUtils';
import EnrollErrorModal from 'bundles/enroll/components/common/EnrollErrorModal';
import PromotionApplicableCheckoutMessage from 'bundles/enroll/components/common/PromotionApplicableCheckoutMessage';
import { useCourseEnrollModalData } from 'bundles/enroll/data/usePageData';
import type { ApiError } from 'bundles/enroll/utils/errorUtils';
import { useEnrollModalEventing } from 'bundles/enroll/utils/eventingUtils';
import { getFreemiumEnrollModalCopy, isFreemiumModelEnabled } from 'bundles/enroll/utils/freemiumModelUtils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import PriceWithDiscountIndicator from 'bundles/payments-common/components/PriceWithDiscountIndicator';
import type { ProductPrices } from 'bundles/payments-common/hooks/useGetProductPrices';
import { certDpURLForCertificatePilotCourse } from 'bundles/premium-certificate/utils/certificatePilotUtils';
import { getPromotionExpirationDate } from 'bundles/promotions/utils/promotionDateUtils';

import _t from 'i18n!nls/enroll-course';

const MODAL_TYPES = keysToConstants(['ENROLL', 'ERROR']);

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

type PropsFromCaller = {
  onClose: () => void;
  s12nId?: string;
};

const styles = {
  radioGroup: css`
    label {
      margin: 0;
    }
  `,
};

type EnrollmentChoiceType = keyof typeof EnrollmentChoiceTypes;

// For prepurchase upsell enrollment flow
export const StandaloneCourseEnrollModalV2 = ({ s12nId, onClose }: PropsFromCaller) => {
  const {
    course,
    s12n,
    enrollmentAvailableChoices,
    promotionEligibilities,
    promotionDetails,
    productPrice,
    partners,
    courseTypeMetadataWithVersion,
  } = useCourseEnrollModalData();
  const { getEnrollModalTrackingRef, trackEnrollModalContinue, trackEnrollModalClose, trackEnrollModalSecondaryCta } =
    useEnrollModalEventing({ isUsingPrePurchaseUpsellFlow: true });
  const { additionalParams } = usePrePurchaseUpsellEnrollment({});

  const freemiumEnrollModalCopy = getFreemiumEnrollModalCopy();

  const visibilityTrackingRef = getEnrollModalTrackingRef();

  const [didClickContinue, setDidClickContinue] = useState<boolean>(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [didJustEnroll, setDidJustEnroll] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<keyof typeof MODAL_TYPES>(MODAL_TYPES.ENROLL);
  const [error, setError] = useState<ApiError | undefined>();
  const [selectedEnrollmentType, setSelectedEnrollmentType] = useState<string | undefined>();

  const { HeadingGroup, Content, Actions } = Dialog;

  const getChoiceTypes = useCallback((): Array<EnrollmentChoiceTypesValues> => {
    const choiceTypes = enrollmentAvailableChoices.choiceTypes ?? [];

    // Don't show the Coursera Plus option for standalone course enroll modal
    return choiceTypes
      .filter((choiceType: EnrollmentChoiceTypesValues) => choiceType !== SUBSCRIBE_TO_COURSERA_PLUS)
      ?.sort((choiceType) => {
        // Enforce that the audit / free enroll option always appears last in the modal
        if (choiceType === AUDIT_COURSE || choiceType === ENROLL_COURSE) {
          return 1;
        }
        return -1;
      });
  }, [enrollmentAvailableChoices.choiceTypes]);

  let reasonCode = enrollmentAvailableChoices.enrollmentChoiceReasonCode;
  if (enrollmentAvailableChoices.isEnrolled && enrollmentAvailableChoices.hasChoice) {
    reasonCode = undefined;
  }

  useEffect(() => {
    if (!selectedEnrollmentType) {
      setSelectedEnrollmentType(getChoiceTypes()[0]);
    }
  }, [getChoiceTypes, selectedEnrollmentType]);

  const handleModalClose = () => {
    if (didJustEnroll) {
      redirect.refresh();
    } else {
      trackEnrollModalClose();
      onClose();
    }
  };

  const onClickContinue = () => {
    setDidClickContinue(true);

    if (certDpURLForCertificatePilotCourse(course.id)) {
      window.open(certDpURLForCertificatePilotCourse(course.id), '_blank');
      return;
    }

    let selectedChoiceType = selectedEnrollmentType;
    if (!selectedEnrollmentType) {
      const choiceTypes = enrollmentAvailableChoices.choiceTypes;

      if (choiceTypes?.length >= 1) {
        selectedChoiceType = choiceTypes[0];
      } else {
        throw new Error('Cannot continue enrollment without selected choice type');
      }
    }

    selectedChoiceType && trackEnrollModalContinue(selectedChoiceType);

    const promoCode = promotionEligibilities?.isEligible ? promotionEligibilities.promoCodeId : undefined;

    const options = {
      courseId: course.id,
    };

    // @ts-expect-error ts-migrate(7015) FIXME: Element implicitly has an 'any' type because index... Remove this comment to see the full error message
    const handleSubmitPromise = choiceTypeToHandleSubmitPromise[selectedChoiceType];
    submitEnrollmentPromise({
      handleSubmitPromise,
      options,
      promoCode,
      additionalParams,
    })
      .then((data: $TSFixMe /* TODO: type submitEnrollmentPromise */) => {
        if (data.didEnroll) {
          setDidJustEnroll(true);
        }
      })
      .catch((data: $TSFixMe /* TODO: type submitEnrollmentPromise */) => {
        setActiveModal(MODAL_TYPES.ERROR);
        setError(data);
      });
  };

  const checkoutAndGoToCourseWithFullDiscount = () => {
    setDidClickContinue(true);

    enrollWithFullDiscountPromotion({
      promotionEligibilities,
    })
      .then(() => {
        redirect.setLocation(course.phoenixHomeLink);
      })
      .catch((data: $TSFixMe /* TODO: type submitEnrollmentPromise */) => {
        setActiveModal(MODAL_TYPES.ERROR);
        setError(data);
      });
  };

  const getLabel = (choiceType: EnrollmentChoiceType) => {
    const courseLabel = courseTypeMetadataWithVersion?.courseLabel;
    const isCourse = courseTypeMetadataWithVersion?.isCourse;
    const renderProductPrice = (descriptionProductPrice?: ProductPrices) => {
      if (!descriptionProductPrice) {
        return null;
      }

      if (
        canPurchaseSingleCourseAndHasFullDiscount({
          enrollmentAvailableChoices,
          promotionDetails,
          promotionEligibilities,
        })
      ) {
        return (
          <PriceWithDiscountIndicator
            amount={descriptionProductPrice.amount}
            finalAmount={0}
            currencyCode={descriptionProductPrice.currencyCode}
            discountColorType="green"
            hideCurrencyCode={true}
            showDiscountPrice={true}
            showDiscountPercent={false}
          />
        );
      } else {
        return (
          <PriceWithDiscountIndicator
            amount={descriptionProductPrice.amount}
            finalAmount={descriptionProductPrice.finalAmount}
            currencyCode={descriptionProductPrice.currencyCode}
            hideCurrencyCode={true}
          />
        );
      }
    };

    switch (choiceType) {
      case PURCHASE_SINGLE_COURSE:
        return (
          <span>
            <FormattedMessage
              message={_t('Purchase course for {productPrice}')}
              productPrice={renderProductPrice(productPrice)}
            />
            {!canPurchaseSingleCourseAndHasFullDiscount({
              promotionDetails,
              promotionEligibilities,
              enrollmentAvailableChoices,
            }) && <PromotionApplicableCheckoutMessage course={course} />}
          </span>
        );
      case ENROLL_COURSE:
        if (courseLabel && !isCourse) {
          return _t('Full #{courseType}, no certificate', { courseType: courseLabel });
        }
        return isFreemiumModelEnabled()
          ? freemiumEnrollModalCopy.fullCourseNoCertificate
          : _t('Full course, no certificate');
      case AUDIT_COURSE:
        return isFreemiumModelEnabled() ? freemiumEnrollModalCopy.auditOnly : _t('Audit only');
      default:
        throw new Error(`Unable to get detect description for ${choiceType}`);
    }
  };

  const getLabelSupportText = (choiceType: EnrollmentChoiceType) => {
    switch (choiceType) {
      case PURCHASE_SINGLE_COURSE:
        if (
          canPurchaseSingleCourseAndHasFullDiscount({
            promotionDetails,
            promotionEligibilities,
            enrollmentAvailableChoices,
          })
        ) {
          const endTimeInMilliseconds = promotionDetails?.endsAt ?? 0;
          return (
            <FormattedMessage
              message={_t('Free enrollment available until {date}. Valid for one enrollment per person.')}
              date={getPromotionExpirationDate(endTimeInMilliseconds, LONG_DATE_ONLY_DISPLAY)}
            />
          );
        } else {
          return _t("Commit to earning a certificate - it's a trusted, shareable way to showcase your new skills.");
        }
      case ENROLL_COURSE:
        return isFreemiumModelEnabled()
          ? freemiumEnrollModalCopy.fullCourseNoCertificateDescription
          : _t('You will still have access to all course materials for this course.');
      case AUDIT_COURSE:
        return isFreemiumModelEnabled()
          ? freemiumEnrollModalCopy.auditOnlyDescription
          : _t('You will have access to all course materials except graded items.');
      default:
        throw new Error(`Unable to get detect description for ${choiceType}`);
    }
  };

  const showViewAllPlans = () => {
    setStep(2);
    trackEnrollModalSecondaryCta('view_all_plans');
  };

  const renderChoice = ({ choiceType }: { choiceType: EnrollmentChoiceTypesValues }) => {
    return (
      <>
        <Radio
          value={choiceType}
          key={choiceType}
          data-testid={choiceType}
          checked={selectedEnrollmentType === choiceType}
          // @ts-expect-error remove once we support elements as label
          label={
            <Typography2 variant="subtitleMedium" component="span">
              {getLabel(choiceType)}
            </Typography2>
          }
          supportText={getLabelSupportText(choiceType)}
        />
      </>
    );
  };

  const renderEnrollmentChoices = (choiceTypes: Array<EnrollmentChoiceTypesValues>) => {
    const shouldShowRadioButton = choiceTypes.length > 1;
    return shouldShowRadioButton ? (
      <RadioGroup
        value={selectedEnrollmentType}
        data-testid="choices"
        css={styles.radioGroup}
        onChange={(e) => setSelectedEnrollmentType(e.target.value)}
        aria-label={_t('Enrollment choice')}
      >
        {choiceTypes.map((choiceType) => renderChoice({ choiceType }))}
      </RadioGroup>
    ) : (
      <div>
        <Typography2 variant="subtitleMedium" component="div">
          {getLabel(choiceTypes[0])}
        </Typography2>
        <Typography2 variant="bodySecondary" component="div">
          {getLabelSupportText(choiceTypes[0])}
        </Typography2>
      </div>
    );
  };

  const renderModalBody = () => {
    if (didJustEnroll) {
      return (
        <Content>
          <TrackedDiv
            trackingName="enrollment_confirmation"
            // eslint-disable-next-line camelcase
            data={{ enrollment_type: selectedEnrollmentType }}
            withVisibilityTracking={true}
            requireFullyVisible={false}
            trackClicks={false}
          >
            <CourseEnrollmentConfirmation courseId={course.id} showCdsDesign={true} />
          </TrackedDiv>
        </Content>
      );
    } else if (reasonCode) {
      return (
        <Content>
          <EnrollmentReasonMessageV2 reasonCode={reasonCode} s12nId={s12nId || s12n?.id} />
        </Content>
      );
    } else {
      let choiceTypes = getChoiceTypes();
      const canPurchaseCourseWithFullDiscount = canPurchaseSingleCourseAndHasFullDiscount({
        promotionDetails,
        promotionEligibilities,
        enrollmentAvailableChoices,
      });

      // Show only purchase course option for courses with 100% promotion. The discount is applied at checkout.
      if (canPurchaseCourseWithFullDiscount) {
        choiceTypes = [PURCHASE_SINGLE_COURSE];
      }

      return (
        <>
          <Content>{renderEnrollmentChoices(choiceTypes)}</Content>
          <Actions>
            {canPurchaseCourseWithFullDiscount ? (
              <TrackedButton
                variant="primary"
                onClick={checkoutAndGoToCourseWithFullDiscount}
                loading={didClickContinue}
                trackingName="course_checkout_with_full_discount_and_go_to_course_button"
                data-e2e="course_checkout_with_full_discount_and_go_to_course_button"
              >
                {_t('Go to course')}
              </TrackedButton>
            ) : (
              <>
                <TrackedButton
                  variant="primary"
                  onClick={onClickContinue}
                  loading={didClickContinue}
                  trackingName="course_enroll_modal_continue_button"
                  data-testid="course_enroll_modal_continue_button"
                >
                  {_t('Continue')}
                </TrackedButton>
                {choiceTypes.length >= 1 && choiceTypes[0] === PURCHASE_SINGLE_COURSE && (
                  <TrackedButton
                    variant="secondary"
                    css={css`
                      margin-left: var(--cds-spacing-200);
                    `}
                    onClick={showViewAllPlans}
                    trackingName="pre_purchase_upsell_course_enroll_modal_view_all_plans_cta"
                    data-testid="pre-purchase-upsell-course-enroll-modal-view-all-plans-cta"
                  >
                    {_t('View all plans')}
                  </TrackedButton>
                )}
              </>
            )}
          </Actions>
        </>
      );
    }
  };

  const getHeaderSubTitle = () => {
    if (s12n) {
      return _t('Part of a #{length}-course series, #{name}', { length: s12n.courseIds.length, name: s12n.name });
    } else if (partners) {
      return getFormattedPartnerDisplayName(partners);
    }
  };

  const renderModalContent = () => {
    return (
      <>
        <HeadingGroup
          // @ts-expect-error remove once we support elements as supportText
          supportText={
            getHeaderSubTitle() ? (
              <Typography2 variant="subtitleMedium" component="h3" data-testid="modal-subheader">
                {getHeaderSubTitle()}
              </Typography2>
            ) : undefined
          }
        >
          {course.name}
        </HeadingGroup>
        {renderModalBody()}
      </>
    );
  };

  if (step === 2) {
    return <PrePurchaseUpsellAllPlansModal onClose={() => setStep(1)} isStandAloneCourse={true} />;
  }

  switch (activeModal) {
    case MODAL_TYPES.ERROR:
      return <EnrollErrorModal error={error} onClose={onClose} />;

    case MODAL_TYPES.ENROLL:
      return (
        <Dialog open onClose={handleModalClose} ref={visibilityTrackingRef}>
          <TrackedDiv
            withVisibilityTracking={true}
            trackingName="pre_purchase_upsell_course_enroll_modal"
            trackingData={{ id: course.id }}
          >
            {renderModalContent()}
          </TrackedDiv>
        </Dialog>
      );

    default:
      return null;
  }
};

export default StandaloneCourseEnrollModalV2;
